import React, { useCallback, useState, useEffect, useReducer } from 'react';
import { Editor } from '@tiptap/react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  List,
  ListOrdered,
  Quote,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  Link,
  Image,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Undo,
  Redo,
  ChevronDown,
  AlertCircle,
  HelpCircle,
  BarChart3,
  MousePointerClick,
  Minus,
  Pilcrow,
  ImagePlus,
  FileText,
  Pencil,
  Wand2,
  Table,
  LayoutGrid,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AIActionType } from './AIContextMenu';

interface TipTapToolbarProps {
  editor: Editor | null;
  className?: string;
  showAIBar?: boolean;
  onAIAction?: (action: AIActionType, options?: { tone?: string; prompt?: string }) => Promise<void>;
  isAIProcessing?: boolean;
}

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  title?: string;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  isActive = false,
  disabled = false,
  children,
  title,
}) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={cn(
      'h-8 w-8 p-0',
      isActive && 'bg-accent text-accent-foreground'
    )}
  >
    {children}
  </Button>
);

const ToolbarDivider: React.FC = () => (
  <div className="w-px h-6 bg-border mx-1" />
);

// ============================================================================
// AI FEATURES BAR (matching the image toolbar)
// ============================================================================

interface AIFeatureButtonProps {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  requiresSelection?: boolean;
  hasSelection?: boolean;
}

function AIFeatureButton({
  icon: Icon,
  label,
  onClick,
  loading = false,
  disabled = false,
  requiresSelection = false,
  hasSelection = false,
}: AIFeatureButtonProps) {
  const isDisabled = disabled || loading || (requiresSelection && !hasSelection);

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={isDisabled}
      className="h-7 gap-1.5 whitespace-nowrap text-xs border-dashed"
    >
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Icon className="h-3.5 w-3.5" />
      )}
      {label}
    </Button>
  );
}

export interface AIFeaturesBarProps {
  editor: Editor | null;
  onAIAction: (action: AIActionType, options?: { tone?: string; prompt?: string }) => Promise<void>;
  isProcessing?: boolean;
  processingAction?: AIActionType | null;
}

export function AIFeaturesBar({ editor, onAIAction, isProcessing = false, processingAction = null }: AIFeaturesBarProps) {
  const [hasSelection, setHasSelection] = useState(false);

  // Track selection state
  useEffect(() => {
    if (!editor) return;

    const updateSelectionState = () => {
      const { from, to } = editor.state.selection;
      setHasSelection(from !== to);
    };

    editor.on('selectionUpdate', updateSelectionState);
    updateSelectionState();

    return () => {
      editor.off('selectionUpdate', updateSelectionState);
    };
  }, [editor]);

  const features = [
    { id: 'generate-ai-image' as AIActionType, label: 'AI Image', icon: ImagePlus },
    { id: 'generate-hero-image' as AIActionType, label: 'Hero Image', icon: ImagePlus },
    { id: 'generate-faq' as AIActionType, label: 'Generate FAQ', icon: HelpCircle },
    { id: 'generate-conclusion' as AIActionType, label: 'Generate Conclusion', icon: FileText },
    { id: 'edit-with-ai' as AIActionType, label: 'Edit with AI', icon: Pencil, requiresSelection: true },
    { id: 'humanize' as AIActionType, label: 'Humanize', icon: Wand2, requiresSelection: true },
    { id: 'generate-list' as AIActionType, label: 'Generate List', icon: List },
    { id: 'generate-table' as AIActionType, label: 'Generate Table', icon: Table },
    { id: 'infographic-diagram' as AIActionType, label: 'Infographic Diagram', icon: LayoutGrid },
  ];

  const handleFeatureClick = (featureId: AIActionType) => {
    // Log AI image generation triggers
    if (featureId === 'generate-ai-image' || featureId === 'generate-hero-image') {
      console.log('[IMAGE_TRACE] [FRONTEND:EDITOR] AI_GEN_TRIGGER - AI image generation triggered from toolbar', {
        actionType: featureId,
        source: 'toolbar',
      });
    }
    onAIAction(featureId);
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 border-b bg-gradient-to-r from-coral-light/50 to-transparent">
      <Sparkles className="h-4 w-4 text-teal mr-1 flex-shrink-0" />
      {features.map((feature) => (
        <AIFeatureButton
          key={feature.id}
          icon={feature.icon}
          label={feature.label}
          onClick={() => handleFeatureClick(feature.id)}
          loading={processingAction === feature.id}
          disabled={isProcessing && processingAction !== feature.id}
          requiresSelection={feature.requiresSelection}
          hasSelection={hasSelection}
        />
      ))}
    </div>
  );
}

// ============================================================================
// MAIN TOOLBAR
// ============================================================================

export function TipTapToolbar({ editor, className, showAIBar = true, onAIAction, isAIProcessing = false }: TipTapToolbarProps) {
  const [processingAction, setProcessingAction] = useState<AIActionType | null>(null);

  // Force re-render when editor selection changes so toolbar state (bold, heading, etc.) updates
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    if (!editor) return;

    // Subscribe to selection and transaction updates to keep toolbar in sync
    editor.on('selectionUpdate', forceUpdate);
    editor.on('transaction', forceUpdate);

    return () => {
      editor.off('selectionUpdate', forceUpdate);
      editor.off('transaction', forceUpdate);
    };
  }, [editor]);

  const handleAIAction = useCallback(async (action: AIActionType, options?: { tone?: string; prompt?: string }) => {
    if (!onAIAction) return;
    setProcessingAction(action);
    try {
      await onAIAction(action, options);
    } finally {
      setProcessingAction(null);
    }
  }, [onAIAction]);
  const setLink = useCallback(() => {
    if (!editor) return;

    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;

    const url = window.prompt('Image URL');
    if (url) {
      console.log('[IMAGE_TRACE] [FRONTEND:EDITOR] URL_INSERT - image URL inserted via prompt', {
        src: url.length > 100 ? `${url.substring(0, 100)}...` : url,
      });
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className={cn('flex flex-col', className)}>
      {/* AI Features Bar */}
      {showAIBar && onAIAction && (
        <AIFeaturesBar
          editor={editor}
          onAIAction={handleAIAction}
          isProcessing={isAIProcessing || processingAction !== null}
          processingAction={processingAction}
        />
      )}

      {/* Main Formatting Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b bg-muted/30">
      {/* Undo/Redo */}
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo"
      >
        <Undo className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo"
      >
        <Redo className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Headings Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 px-2 gap-1">
            {editor.isActive('heading', { level: 1 }) && 'Heading 1'}
            {editor.isActive('heading', { level: 2 }) && 'Heading 2'}
            {editor.isActive('heading', { level: 3 }) && 'Heading 3'}
            {editor.isActive('heading', { level: 4 }) && 'Heading 4'}
            {!editor.isActive('heading') && 'Paragraph'}
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            onClick={() => editor.chain().focus().setParagraph().run()}
          >
            Paragraph
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          >
            <Heading1 className="h-4 w-4 mr-2" /> Heading 1
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            <Heading2 className="h-4 w-4 mr-2" /> Heading 2
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            <Heading3 className="h-4 w-4 mr-2" /> Heading 3
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
          >
            <Heading4 className="h-4 w-4 mr-2" /> Heading 4
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ToolbarDivider />

      {/* Text Formatting */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Bold"
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Italic"
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        title="Underline"
      >
        <Underline className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive('strike')}
        title="Strikethrough"
      >
        <Strikethrough className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        isActive={editor.isActive('code')}
        title="Inline Code"
      >
        <Code className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Lists */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <List className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        title="Blockquote"
      >
        <Quote className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Alignment */}
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        isActive={editor.isActive({ textAlign: 'left' })}
        title="Align Left"
      >
        <AlignLeft className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        isActive={editor.isActive({ textAlign: 'center' })}
        title="Align Center"
      >
        <AlignCenter className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        isActive={editor.isActive({ textAlign: 'right' })}
        title="Align Right"
      >
        <AlignRight className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        isActive={editor.isActive({ textAlign: 'justify' })}
        title="Justify"
      >
        <AlignJustify className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Links & Images */}
      <ToolbarButton
        onClick={setLink}
        isActive={editor.isActive('link')}
        title="Add Link"
      >
        <Link className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton onClick={addImage} title="Add Image">
        <Image className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal Rule"
      >
        <Minus className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Custom Blocks Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 px-2 gap-1">
            Insert Block
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            onClick={() => editor.chain().focus().toggleCallout({ type: 'info' }).run()}
          >
            <AlertCircle className="h-4 w-4 mr-2 text-blue-500" /> Info Callout
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => editor.chain().focus().toggleCallout({ type: 'warning' }).run()}
          >
            <AlertCircle className="h-4 w-4 mr-2 text-yellow-500" /> Warning Callout
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => editor.chain().focus().toggleCallout({ type: 'success' }).run()}
          >
            <AlertCircle className="h-4 w-4 mr-2 text-green-500" /> Success Callout
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => editor.chain().focus().toggleCallout({ type: 'error' }).run()}
          >
            <AlertCircle className="h-4 w-4 mr-2 text-red-500" /> Error Callout
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => (editor.commands as unknown as { insertFaq: () => boolean }).insertFaq?.()}
          >
            <HelpCircle className="h-4 w-4 mr-2" /> FAQ Block
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => (editor.commands as unknown as { insertStatsGrid: (cols?: number) => boolean }).insertStatsGrid?.(3)}
          >
            <BarChart3 className="h-4 w-4 mr-2" /> Stats Grid
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => (editor.commands as unknown as { insertCta: () => boolean }).insertCta?.()}
          >
            <MousePointerClick className="h-4 w-4 mr-2" /> Call to Action
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      </div>
    </div>
  );
}

export default TipTapToolbar;
